'use client';

import { useRouter } from 'next/navigation';
import { useCallback, useState } from 'react';

export type IStepActionStatus = 'idle' | 'running' | 'done' | 'error';

interface IUseStepActionParams {
  start: (force: boolean) => Promise<Response>;
  // When true, the action is a resumable batch: keep re-POSTing while the stream ends in
  // `[continue]`, stop on `[result]`/`[error]`, and surface a stream that ended with no terminal
  // marker (the Worker CPU/time limit hard-kills the isolate silently). `force` is sent only on
  // the first request so a continuation never wipes the server-side progress cursor.
  autoResume?: boolean;
  initialForce?: boolean;
}

interface IUseStepActionReturn {
  status: IStepActionStatus;
  log: string;
  run: () => Promise<void>;
  reset: () => void;
}

// Safety net so a pathological zero-progress batch can never loop forever (the server phases are
// designed to always advance; this only ever trips on a genuine backend bug).
const MAX_RESUME_ITERATIONS = 1000;

type ITerminal = 'result' | 'continue' | 'error' | 'unexpected';

function classifyTerminal(text: string): ITerminal {
  const lines = text.trimEnd().split('\n');
  for (let i = lines.length - 1; i >= 0; i--) {
    const line = (lines[i] ?? '').trim();
    if (line.startsWith('[result]')) {
      return 'result';
    }
    if (line.startsWith('[continue]')) {
      return 'continue';
    }
    if (line.startsWith('[error]')) {
      return 'error';
    }
    if (line.length > 0) {
      // A non-empty last line that is not a terminal marker means the stream was cut off.
      return 'unexpected';
    }
  }
  return 'unexpected';
}

async function readStream(
  body: ReadableStream<Uint8Array>,
  append: (text: string) => void,
): Promise<string> {
  const reader = body.getReader();
  const decoder = new TextDecoder();
  let text = '';
  for (;;) {
    const { done, value } = await reader.read();
    if (done) {
      return text;
    }
    const chunk = decoder.decode(value, { stream: true });
    text += chunk;
    append(chunk);
  }
}

interface IFinalizeCtx {
  append: (text: string) => void;
  setStatus: (status: IStepActionStatus) => void;
  onDone: () => void;
}

// Decides what to do after one stream completes: 'continue' asks the caller to re-POST; 'stop'
// means the run is over (this already set the terminal status + any message).
function finalizeStream(
  chunk: string,
  autoResume: boolean,
  ctx: IFinalizeCtx,
): 'continue' | 'stop' {
  const terminal = autoResume ? classifyTerminal(chunk) : 'result';
  if (terminal === 'continue') {
    return 'continue';
  }
  if (terminal === 'result') {
    ctx.setStatus('done');
    ctx.onDone();
    return 'stop';
  }
  if (terminal === 'unexpected') {
    ctx.append(
      '\n[error] Stream ended without finishing (likely the Worker CPU/time limit). Click Run again to resume.\n',
    );
  }
  ctx.setStatus('error');
  return 'stop';
}

// Runs the resumable POST loop: one bounded batch per request, re-POSTing while the stream ends
// in `[continue]` (dropping `force` after the first), until a terminal marker or the iteration cap.
async function runResumeLoop(
  start: (force: boolean) => Promise<Response>,
  autoResume: boolean,
  initialForce: boolean,
  ctx: IFinalizeCtx,
): Promise<void> {
  let force = initialForce;
  for (let iteration = 0; iteration < MAX_RESUME_ITERATIONS; iteration++) {
    const res = await start(force);
    if (!res.body) {
      ctx.append('[error] no response stream\n');
      ctx.setStatus('error');
      return;
    }
    const chunk = await readStream(res.body, ctx.append);
    if (finalizeStream(chunk, autoResume, ctx) === 'stop') {
      return;
    }
    force = false;
  }
  ctx.append(
    `\n[error] Stopped after ${MAX_RESUME_ITERATIONS} resume steps without finishing. Click Run again to continue.\n`,
  );
  ctx.setStatus('error');
}

export function useStepAction({
  start,
  autoResume = false,
  initialForce = false,
}: IUseStepActionParams): IUseStepActionReturn {
  const router = useRouter();
  const [status, setStatus] = useState<IStepActionStatus>('idle');
  const [log, setLog] = useState('');

  const run = useCallback(async () => {
    setStatus('running');
    setLog('');
    const append = (text: string): void => setLog((prev) => prev + text);
    const ctx: IFinalizeCtx = { append, setStatus, onDone: () => router.refresh() };
    try {
      await runResumeLoop(start, autoResume, initialForce, ctx);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Unknown error';
      append(`\n[error] ${message}\n`);
      setStatus('error');
    }
  }, [start, router, autoResume, initialForce]);

  const reset = useCallback(() => {
    setStatus('idle');
    setLog('');
  }, []);

  return { status, log, run, reset };
}
